import React, { useEffect } from 'react';
import { Controller, useForm, useFormState } from 'react-hook-form';
import { VoidFunctionType } from '../../../core/types/coreTypes';
import ButtonActions from '../../../core/components/button-actions/ButtonActions';
import Modal from '../../../core/components/modal/Modal';
import { ErrorsEnum } from '../../../core/enums/errorsEnum';
import { maxLengthMessageHandle } from '../../../core/utils/errorMessageHandle';
import Input from '../../../core/components/input/Input';
import Textarea from '../../../core/components/textarea/Textarea';
import Select from '../../../core/components/select/Select';
import { AddInfoMessageFormFields, IInfoMessage } from '../types/infoMessagesTypes';
import { getDisplayOrders } from '../utils/displayOrder';
import {
  editInfoMessageThunk,
  getInfoMessageMaxDisplayOrder,
  getInfoMessagesList,
} from '../../../store/thunks/settings/infoMessagesThunks';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { infoMessagesListFiltersSelector } from '../../../store/selectors/settingsSelectors';
import { isGlobalLoadingSelector } from '../../../store/selectors/coreSelectors';

type EditMessageModalProps = {
  isVisible: boolean,
  onCancel: VoidFunctionType,
  maxDisplayOrder: number,
  infoMessage: IInfoMessage | null,
}

const EditInfoMessageModal: React.FC<EditMessageModalProps> = ({
  isVisible, onCancel, maxDisplayOrder, infoMessage,
}) => {
  const dispatch = useAppDispatch();
  const infoMessageListFilters = useAppSelector(infoMessagesListFiltersSelector);
  const isGlobalLoading = useAppSelector(isGlobalLoadingSelector);

  const {
    control, handleSubmit, setError, setValue, reset,
  } = useForm<AddInfoMessageFormFields>({
    defaultValues: {
      title: '',
      message: '',
      displayOrder: undefined,
    },
    mode: 'all',
  });
  const { errors } = useFormState({ control });

  useEffect(() => {
    setValue('title', infoMessage?.title || '');
    setValue('message', infoMessage?.message || '');
    setValue('displayOrder', infoMessage?.displayOrder || undefined);
    // eslint-disable-next-line
  }, [infoMessage, isVisible]);

  const onCloseHandler = () => {
    reset();
    onCancel();
  };

  const updateState = () => {
    dispatch(getInfoMessagesList({ filters: infoMessageListFilters }));
    dispatch(getInfoMessageMaxDisplayOrder());
  };

  const onSaveHandler = (values: AddInfoMessageFormFields) => {
    infoMessage?.id && dispatch(editInfoMessageThunk(
      {
        id: infoMessage?.id,
        data: {
          title: values.title.trim(),
          message: values.message.trim(),
          displayOrder: values.displayOrder as number,
        },
        onClose: onCloseHandler,
        updateState,
      },
    ));
  };

  return (
    <Modal
      title="Edit info message"
      visible={isVisible}
      onCancel={onCancel}
      width={700}
      destroyOnClose
    >
      <form onSubmit={handleSubmit(onSaveHandler)}>
        <div className="infoMessagesForm">
          <Controller
            control={control}
            name="title"
            rules={{
              required: ErrorsEnum.REQUIRED,
              maxLength: { value: 100, message: maxLengthMessageHandle(100) },
            }}
            render={({ field }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                onBlur={(e) => {
                  const v = e.target.value;
                  if (!v.trim()) {
                    setValue('title', '');
                    setError('title', { type: 'required', message: ErrorsEnum.REQUIRED });
                  }
                }}
                error={errors?.title?.message}
                label="Title"
              />
            )}
          />
          <Controller
            name="message"
            control={control}
            rules={{
              required: ErrorsEnum.REQUIRED,
              maxLength: { value: 500, message: maxLengthMessageHandle(500) },
            }}
            render={({ field }) => (
              <Textarea
                value={field.value}
                onChange={field.onChange}
                onBlur={(e) => {
                  const v = e.target.value;
                  if (!v.trim()) {
                    setValue('message', '');
                    setError('message', { type: 'required', message: ErrorsEnum.REQUIRED });
                  }
                }}
                error={errors?.message?.message}
                label="Message"
              />
            )}
          />
          <Controller
            control={control}
            name="displayOrder"
            rules={{
              required: ErrorsEnum.REQUIRED,
            }}
            render={({ field }) => (
              <Select
                options={getDisplayOrders(maxDisplayOrder)}
                value={field.value}
                onChange={field.onChange}
                error={errors?.displayOrder?.message}
                label="Display order"
                parentRender
              />
            )}
          />
        </div>
        <ButtonActions
          createLabel="Save"
          cancelLabel="Cancel"
          createType="submit"
          cancelClick={onCloseHandler}
          isLoading={isGlobalLoading}
        />
      </form>
    </Modal>
  );
};

export default EditInfoMessageModal;
